/**
 * Kickard PHASE 5.0A PASS 3A — Read-only Match Summary route.
 *
 * Renders the SAME finished match summary the player just viewed in
 * the live game screen, from a sessionStorage snapshot keyed by
 * matchId. Used exclusively as the Back destination when Luck Report
 * was opened from a post-match summary — this fixes the production
 * bug where Back could land on the legacy Online / Quick lobby
 * because the live WebSocket / room state had already been released.
 *
 * SAFETY:
 *   • The route is READ-ONLY. No websocket, no re-finalization, no
 *     SR recalculation, no Weekly Cup emission, no telemetry
 *     finalization, no match-history mutation.
 *   • Rematch / Queue Again controls are intentionally NOT re-armed
 *     on the restored view. The user has left the live match; Home,
 *     Play Hub and Luck Report are the safe onward actions.
 *   • If no snapshot exists (page reload, cross-device, cleared
 *     session), the route redirects home instead of crashing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MatchSummaryView from '@/screens/MatchSummaryView';

export default function MatchSummaryRoute() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`kickard.summary.${matchId}`);
      if (!raw) { setMissing(true); return; }
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.state) { setMissing(true); return; }
      setSnap(parsed);
    } catch (_) {
      setMissing(true);
    }
  }, [matchId]);

  const empty = useMemo(() => (
    <div
      data-testid="match-summary-route-missing"
      className="min-h-[100dvh] w-full bg-black text-white flex flex-col items-center justify-center px-6"
    >
      <div className="font-heading text-2xl tracking-[0.18em] uppercase text-white mb-2">
        Summary Unavailable
      </div>
      <div className="text-white/50 text-sm text-center max-w-[320px] mb-6">
        This match summary is no longer cached on this device.
      </div>
      <button
        data-testid="match-summary-route-home-btn"
        onClick={() => navigate('/home')}
        className="btn-primary"
        style={{ padding: '10px 22px', fontSize: 16, letterSpacing: '0.18em' }}
      >
        HOME
      </button>
    </div>
  ), [navigate]);

  if (missing) return empty;
  if (!snap) return null;

  return (
    <div data-testid="match-summary-route" data-match-id={matchId}>
      <MatchSummaryView
        state={snap.state}
        myPlayerId={snap.myPlayerId}
        isOnline={snap.isOnline}
        opponentName={snap.opponentName}
        matchType={snap.matchType}
        myName={snap.myName}
        myGuestId={snap.myGuestId}
        h2h={snap.h2h ?? null}
        h2hUpdating={snap.h2hUpdating === true}
        // Rematch controls not re-armed — the room may have released.
        canDirectRematch={false}
        onRematch={snap.isOnline ? null : () => navigate('/play')}
        onRematchRequest={null}
        onRematchAccept={null}
        onRematchDecline={null}
        onHome={() => navigate('/home')}
      />
    </div>
  );
}
